import { useState, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { LogOut, RefreshCw, Phone, Mail, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  artist_inquiry: "Artist", ai_inquiry: "AI Project", audit_submission: "Audit",
  booking: "Booking", chat_summary: "Chat", job_application: "Job App",
  creator_inquiry: "Creator", brand_inquiry: "Brand",
  model_registration: "Model Reg", model_brand_inquiry: "Model Brief"
};

const TYPE_COLORS = {
  artist_inquiry: "bg-purple-500/20 text-purple-300", ai_inquiry: "bg-blue-500/20 text-blue-300",
  audit_submission: "bg-green-500/20 text-green-300", booking: "bg-yellow-500/20 text-yellow-300",
  chat_summary: "bg-pink-500/20 text-pink-300", job_application: "bg-cyan-500/20 text-cyan-300",
  creator_inquiry: "bg-orange-500/20 text-orange-300", brand_inquiry: "bg-red-500/20 text-red-300",
  model_registration: "bg-fuchsia-500/20 text-fuchsia-300", model_brand_inquiry: "bg-rose-500/20 text-rose-300"
};

const HIDDEN_KEYS = new Set(["id", "_id", "type", "status", "created_at", "target_email", "session_id", "message_count"]);

// Security: use sessionStorage instead of localStorage (cleared on tab close, not persistent)
function getStoredToken() { try { return sessionStorage.getItem("admin_token"); } catch { return null; } }
function setStoredToken(t) { try { sessionStorage.setItem("admin_token", t); } catch { /* noop */ } }
function clearStoredToken() { try { sessionStorage.removeItem("admin_token"); } catch { /* noop */ } }

export default function AdminPage() {
  const [token, setToken] = useState(getStoredToken);
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [logging, setLogging] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const logout = useCallback(() => { clearStoredToken(); setToken(null); setInquiries([]); }, []);

  const fetchInquiries = useCallback(async (authToken) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/inquiries`, { headers: { Authorization: `Bearer ${authToken}` } });
      setInquiries(r.data.inquiries);
    } catch (err) {
      if (err.response?.status === 401) { logout(); toast.error("Session expired"); }
      else { console.error("Fetch inquiries failed:", err.message); }
    }
    setLoading(false);
  }, [logout]);

  useEffect(() => { if (token) fetchInquiries(token); }, [token, fetchInquiries]);

  const login = async (e) => {
    e.preventDefault();
    setLogging(true);
    try {
      const r = await axios.post(`${API}/auth/login`, { email, password: pass });
      setStoredToken(r.data.token);
      setToken(r.data.token);
      toast.success("Logged in");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    }
    setLogging(false);
  };

  const updateStatus = useCallback(async (id, type, status) => {
    try {
      await axios.put(`${API}/admin/inquiries/${id}/status`, { type, status }, { headers: { Authorization: `Bearer ${token}` } });
      setInquiries(prev => prev.map(i => i.id === id ? { ...i, status } : i));
      toast.success(`Marked as ${status}`);
    } catch (err) { toast.error("Failed to update"); console.error(err.message); }
  }, [token]);

  if (!token) return <LoginForm email={email} setEmail={setEmail} pass={pass} setPass={setPass} logging={logging} onSubmit={login} />;
  return <Dashboard inquiries={inquiries} loading={loading} filter={filter} setFilter={setFilter} expanded={expanded} setExpanded={setExpanded} onRefresh={() => fetchInquiries(token)} onLogout={logout} updateStatus={updateStatus} token={token} />;
}

function LoginForm({ email, setEmail, pass, setPass, logging, onSubmit }) {
  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <h1 className="text-2xl tracking-tight mb-2" data-testid="admin-login-title">Admin</h1>
        <p className="text-zinc-500 text-xs tracking-wider mb-10">Prospera.buzz Dashboard</p>
        <form onSubmit={onSubmit} className="space-y-4">
          <Input data-testid="admin-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="Email" className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 h-11 rounded-none text-xs tracking-wider" />
          <Input data-testid="admin-password" type="password" value={pass} onChange={e => setPass(e.target.value)}
            placeholder="Password" className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 h-11 rounded-none text-xs tracking-wider" />
          <Button type="submit" disabled={logging} data-testid="admin-login-btn"
            className="bg-white text-black hover:bg-zinc-200 rounded-none w-full h-11 text-xs tracking-[0.2em] disabled:opacity-50">
            {logging ? "Logging in..." : "Login"}
          </Button>
        </form>
      </motion.div>
    </div>
  );
}

function Dashboard({ inquiries, loading, filter, setFilter, expanded, setExpanded, onRefresh, onLogout, updateStatus }) {
  const types = useMemo(() => [...new Set(inquiries.map(i => i.type))], [inquiries]);
  const filtered = useMemo(() => filter === "all" ? inquiries : inquiries.filter(i => i.type === filter), [inquiries, filter]);
  const pending = useMemo(() => inquiries.filter(i => !i.status || i.status === "received" || i.status === "stored" || i.status === "pending" || i.status === "pending_approval").length, [inquiries]);
  const today = useMemo(() => {
    const todayStr = new Date().toDateString();
    return inquiries.filter(i => new Date(i.created_at).toDateString() === todayStr).length;
  }, [inquiries]);

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="px-6 md:px-12 py-4 border-b border-white/10 flex items-center justify-between mt-16" data-testid="admin-header">
        <p className="text-xs tracking-[0.1em]">Prospera.buzz Admin</p>
        <div className="flex items-center gap-4">
          <button onClick={onRefresh} className="text-zinc-500 hover:text-white transition-colors" data-testid="admin-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button onClick={onLogout} className="text-zinc-500 hover:text-white transition-colors flex items-center gap-2 text-[10px] tracking-wider" data-testid="admin-logout">
            <LogOut className="w-3 h-3" /> Logout
          </button>
        </div>
      </div>
      <div className="px-6 md:px-12 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[{ n: inquiries.length, l: "Total inquiries" }, { n: today, l: "New today" }, { n: pending, l: "Pending" }, { n: inquiries.length - pending, l: "Handled" }].map(s => (
          <div key={s.l} className="bg-[#0A0A0A] border border-white/10 p-4" data-testid={`stat-${s.l.replace(/\s/g, "-")}`}>
            <p className="text-xl md:text-2xl">{s.n}</p>
            <p className="text-[10px] text-zinc-600 mt-1 tracking-[0.15em]">{s.l}</p>
          </div>
        ))}
      </div>
      <div className="px-6 md:px-12 py-4 flex flex-wrap gap-2 border-b border-white/10">
        <FilterChip label={`All (${inquiries.length})`} active={filter === "all"} onClick={() => setFilter("all")} testId="filter-all" />
        {types.map(t => (
          <FilterChip key={t} label={`${TYPE_LABELS[t] || t} (${inquiries.filter(i => i.type === t).length})`}
            active={filter === t} onClick={() => setFilter(t)} testId={`filter-${t}`} />
        ))}
      </div>
      <div className="px-6 md:px-12 py-6 space-y-2">
        {loading && <p className="text-zinc-500 text-xs tracking-wider">Loading...</p>}
        {!loading && filtered.length === 0 && <p className="text-zinc-600 text-xs tracking-wider">No inquiries yet.</p>}
        {filtered.map(inq => (
          <InquiryCard key={inq.id} inq={inq} expanded={expanded === inq.id} onToggle={() => setExpanded(expanded === inq.id ? null : inq.id)} updateStatus={updateStatus} />
        ))}
      </div>
    </div>
  );
}

function FilterChip({ label, active, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`text-[10px] tracking-wider px-4 py-2 border transition-colors ${active ? "bg-white text-black border-white" : "border-white/15 text-zinc-400 hover:border-white/40"}`}>
      {label}
    </button>
  );
}

function InquiryCard({ inq, expanded, onToggle, updateStatus }) {
  const statusColor = inq.status === "contacted" ? "bg-blue-500/20 text-blue-300" : inq.status === "closed" ? "bg-green-500/20 text-green-300" : "bg-zinc-800 text-zinc-400";
  const details = useMemo(() => Object.entries(inq).filter(([k, v]) => !HIDDEN_KEYS.has(k) && v), [inq]);
  const contactPhone = inq.phone || inq.visitor_phone;
  const contactEmail = inq.email || inq.visitor_email;

  return (
    <div className="bg-[#0A0A0A] border border-white/10 hover:border-white/20 transition-colors" data-testid={`inquiry-${inq.id}`}>
      <button onClick={onToggle} className="w-full text-left p-4 flex items-center gap-4">
        <span className={`text-[9px] tracking-wider px-2 py-1 ${TYPE_COLORS[inq.type] || "bg-zinc-800 text-zinc-400"}`}>
          {TYPE_LABELS[inq.type] || inq.type}
        </span>
        <span className="text-xs tracking-wider flex-1 min-w-0 truncate">{inq.name || inq.visitor_name || inq.contact_name || "—"}</span>
        <span className="text-[10px] text-zinc-600 tracking-wider hidden sm:block">{contactPhone || "—"}</span>
        <span className="text-[10px] text-zinc-700 tracking-wider hidden md:block">{new Date(inq.created_at).toLocaleDateString()}</span>
        <span className={`text-[9px] tracking-wider px-2 py-1 ${statusColor}`}>{inq.status || "new"}</span>
        <ChevronRight className={`w-3 h-3 text-zinc-600 transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-white/5 pt-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {details.map(([k, v]) => (
              <div key={k}>
                <p className="text-[9px] text-zinc-600 tracking-wider">{k.replace(/_/g, " ")}</p>
                <p className="text-[10px] text-zinc-300 tracking-wider mt-0.5 break-words">{typeof v === "string" ? v.slice(0, 200) : JSON.stringify(v).slice(0, 200)}</p>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 pt-2">
            <p className="text-[9px] text-zinc-600 tracking-wider">{new Date(inq.created_at).toLocaleString()}</p>
            {contactPhone && <a href={`tel:${contactPhone}`} className="text-[10px] text-zinc-400 hover:text-white flex items-center gap-1 transition-colors"><Phone className="w-3 h-3" /> Call</a>}
            {contactEmail && <a href={`mailto:${contactEmail}`} className="text-[10px] text-zinc-400 hover:text-white flex items-center gap-1 transition-colors"><Mail className="w-3 h-3" /> Email</a>}
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={() => updateStatus(inq.id, inq.type, "contacted")} data-testid={`mark-contacted-${inq.id}`}
              className="text-[10px] tracking-wider px-4 py-2 border border-blue-500/30 text-blue-400 hover:bg-blue-500/10 transition-colors">Mark contacted</button>
            <button onClick={() => updateStatus(inq.id, inq.type, "closed")} data-testid={`mark-closed-${inq.id}`}
              className="text-[10px] tracking-wider px-4 py-2 border border-green-500/30 text-green-400 hover:bg-green-500/10 transition-colors">Mark closed</button>
          </div>
        </div>
      )}
    </div>
  );
}
